/**
 * INLINE MADDE AKSİYONLARI (CLOS-1256 — DOCX Kanonu §10, §11).
 *
 * ### ⚠ Aksiyonlar GERÇEK DOCX'i değiştiriyor
 *
 * Kanon §10.3: *"Bu aksiyonlar gerçek DOCX yapısını değiştirmelidir. Sahte sidebar clause
 * listesi değişip document değişmeden kalamaz."* Bu yüzden hiçbir aksiyon kendi listesini
 * güncellemiyor: hepsi motorun **otomasyon protokolüne** komut yazıyor, liste belgeden
 * yeniden okunuyor.
 *
 * ### ⚠ Neden ayrı sayfa değil
 *
 * Kanon §10.1 `Madde Ekle`/`Düzenle`/`Sil` için ayrı bir route'u normal yol olmaktan
 * açıkça men ediyor. Aksiyonlar belgenin yanındaki panelde; belgeyi terk etmeden.
 *
 * ### ⚠ TAŞIMA BU DİLİMDE YOK — ölçülmüş bir sınır
 *
 * Otomasyon komut sözlüğünde (`AUTOMATION_COMMAND_OPERATIONS`) blok taşıyan bir komut yok.
 * `replaceStoryBlocks` var ama kendi tanımına göre *"removes tables, content controls,
 * section marks, comments and revisions"* — bir maddeyi taşımak için belgenin geri kalanını
 * feda ederdi. Sil+yeniden ekle ile taşımak ise **kimliği yok eder** (§11) ve redline o
 * maddeyi "silindi + eklendi" gösterirdi. Yani taşıma, yanlış yapılırsa müzakere kaydını
 * bozan bir aksiyon; doğrusu gelene kadar YAPILMIYOR ve kartta yazıyor.
 */

/** Motorun otomasyon host'unun yalnız kullandığımız yüzü. */
export type OtomasyonHost = {
  execute: (istek: { operations: readonly Record<string, unknown>[] }) => {
    ok: boolean;
    results: readonly { status: string; value?: unknown; error?: { message?: string } }[];
  };
};

type Handle = Record<string, unknown>;

/**
 * Otomasyon değerlerini açar.
 *
 * ⚠ İKİ sarmal var ve ölçülerek öğrenildi: tekil sonuç `{kind:"handle", handle}`, liste
 * sonucu `{kind:"handles", handles:[...]}`. Yalnız tekili açan bir okuyucu, paragraf
 * listesini BOŞ görür ve panel hiçbir madde göstermezdi — ilk yazımda tam bu oldu.
 */
function deger(sonuc: { status: string; value?: unknown } | undefined): unknown {
  if (!sonuc || sonuc.status !== "ok") return null;
  const v = sonuc.value as { kind?: string; handle?: unknown; handles?: unknown } | undefined;
  if (!v || typeof v !== "object") return v;
  if (v.kind === "handle") return v.handle;
  if (v.kind === "handles") return v.handles;
  /** ⚠ Skaler değerler de sarmalı: `{kind:"style", name}` ve `{kind:"text", text}`. */
  if (v.kind === "style") return (v as { name?: string | null }).name ?? null;
  if (v.kind === "text") return (v as { text?: string }).text ?? "";
  return v;
}

export type MaddeSatiri = {
  /** Başlık paragrafının handle'ı — aksiyonların çapası. */
  handle: Handle;
  baslik: string;
  /** `heading 1` → 1. Alt madde 2. */
  seviye: number;
  /** Bu maddenin gövde paragrafları (başlık hariç) — silme hepsini kapsıyor. */
  govde: Handle[];
};

const BASLIK_ADI = (seviye: number) => `heading ${seviye}`;

/**
 * Belgedeki maddeleri okur.
 *
 * ⚠ Madde = `heading 1`/`heading 2` stilli paragraf. Yazıcımızın kuralı bu (`Heading1`
 * madde başlığı) ve dışarıdan gelen Word belgelerinde de başlık stili aynı şeyi anlatıyor.
 */
export function maddeleriOku(host: OtomasyonHost): MaddeSatiri[] {
  const doc = deger(host.execute({ operations: [{ op: "getDocument" }] }).results[0]) as Handle;
  if (!doc) return [];
  const body = deger(
    host.execute({ operations: [{ op: "getBody", document: doc }] }).results[0],
  ) as Handle;
  if (!body) return [];

  const ham = deger(host.execute({ operations: [{ op: "getParagraphs", body }] }).results[0]);
  const paragraflar = (Array.isArray(ham) ? ham : []) as Handle[];

  const satirlar: MaddeSatiri[] = [];
  for (const paragraf of paragraflar) {
    const stil = deger(
      host.execute({ operations: [{ op: "getStyle", span: { paragraph: paragraf } }] }).results[0],
    );
    const ad = typeof stil === "string" ? stil : null;
    const seviye = ad ? Number(/(\d+)\s*$/.exec(ad)?.[1]) : NaN;
    const baslikMi = Boolean(ad && /^heading/i.test(ad) && Number.isFinite(seviye));

    if (baslikMi) {
      const metin = deger(
        host.execute({ operations: [{ op: "getText", target: paragraf }] }).results[0],
      );
      satirlar.push({
        handle: paragraf,
        baslik: typeof metin === "string" ? metin : "",
        seviye,
        govde: [],
      });
      continue;
    }
    /** ⚠ Başlıksız paragraf, en son maddenin gövdesi. Madde yoksa panel onu göstermiyor. */
    satirlar[satirlar.length - 1]?.govde.push(paragraf);
  }
  return satirlar;
}

function calistir(host: OtomasyonHost, operations: Record<string, unknown>[]): boolean {
  return host.execute({ operations }).ok;
}

/**
 * Madde ekler (üste / alta / alt madde).
 *
 * ⚠ İki komut TEK batch'te: paragraf eklenip stili ayrı bir turda verilseydi, araya düşen
 * bir hata belgede stilsiz bir paragraf bırakırdı — kullanıcının eklemediği bir satır.
 */
export function maddeEkle(
  host: OtomasyonHost,
  girdi: { anchor: Handle; nere: "before" | "after"; baslik: string; seviye: number },
): boolean {
  const ekleme = host.execute({
    operations: [
      {
        op: "insertParagraph",
        anchor: { paragraph: girdi.anchor },
        where: girdi.nere,
        text: girdi.baslik,
      },
    ],
  });
  const yeni = deger(ekleme.results[0]) as Handle | null;
  if (!ekleme.ok || !yeni) return false;
  return calistir(host, [
    { op: "setStyle", span: { paragraph: yeni }, name: BASLIK_ADI(girdi.seviye) },
  ]);
}

/**
 * Maddeyi ve gövdesini siler.
 *
 * ⚠ Gövde de siliniyor: yalnız başlığı silmek, maddenin metnini bir önceki maddenin altına
 * TAŞIRDI — kullanıcının istemediği ve fark etmeyeceği bir birleşme.
 *
 * ⚠ Sondan başa siliniyor: baştan silmek kalan handle'ların çözümünü bozabilir.
 */
export function maddeSil(host: OtomasyonHost, madde: MaddeSatiri): boolean {
  const hedefler = [...madde.govde, madde.handle].reverse();
  return calistir(
    host,
    hedefler.map((paragraph) => ({ op: "deleteParagraph", paragraph })),
  );
}

/**
 * Madde başlığını değiştirir.
 *
 * ⚠ Paragraf SİLİNİP yeniden yazılmıyor: silme kimliği (`w:bookmark`) de götürürdü ve
 * redline o maddeyi "silindi + eklendi" gösterirdi. Metin, paragrafın kendi span'ı
 * üzerinden değiştiriliyor.
 */
export function basligiDegistir(
  host: OtomasyonHost,
  madde: MaddeSatiri,
  yeniBaslik: string,
): boolean {
  return calistir(host, [
    { op: "replaceSpan", span: { paragraph: madde.handle }, text: yeniBaslik },
  ]);
}

/** Madde seviyesini değiştirir (`heading 1` ↔ `heading 2`) — kimlik korunuyor. */
export function seviyeDegistir(host: OtomasyonHost, madde: MaddeSatiri, seviye: number): boolean {
  return calistir(host, [
    { op: "setStyle", span: { paragraph: madde.handle }, name: BASLIK_ADI(seviye) },
  ]);
}

/**
 * Maddeyi çoğaltır.
 *
 * ⚠ Kopya YENİ KİMLİK alıyor ve bu bilinçli (§11): aynı kimlikten iki blok, ona bağlı her
 * çapayı (yorum, müzakere, AI bulgusu) ikiye bölerdi. Çoğaltma zaten yeni bir madde
 * yaratmak demek — kimliği de yeni olmalı. Yeni paragraflar yer imi taşımıyor; kaydetme
 * yolunda "eklenmiş" olarak işaretleniyorlar (CLOS-1255).
 */
export function maddeCogalt(
  host: OtomasyonHost,
  madde: MaddeSatiri,
  govdeMetinleri: string[],
): boolean {
  /**
   * ⚠ SIRA TERS: önce gövde, sonra başlık — ölçülerek bulundu.
   *
   * `insertParagraph` yeni paragrafa ÇAPANIN biçimini miras bırakıyor. Doğal sıra (önce
   * başlık, sonra gövde) denendiğinde kopyanın gövde paragrafı da başlık stili aldı ve
   * belgede iki başlık göründü. Gövdeyi ORİJİNAL gövdenin ardına eklemek, gövde biçimini
   * doğru yerden miras aldırıyor; başlık en sona, yeni gövdenin ÖNÜNE giriyor.
   */
  let capa = madde.govde.at(-1) ?? madde.handle;
  const eklenenGovde: Handle[] = [];
  for (const metin of govdeMetinleri) {
    const sonuc = host.execute({
      operations: [
        { op: "insertParagraph", anchor: { paragraph: capa }, where: "after", text: metin },
      ],
    });
    const yeni = deger(sonuc.results[0]) as Handle | null;
    if (!sonuc.ok || !yeni) return false;
    eklenenGovde.push(yeni);
    capa = yeni;
  }

  const baslikCapasi = eklenenGovde[0];
  if (!baslikCapasi) {
    /** Gövdesiz madde: kopyası da gövdesiz; başlık doğrudan maddenin ardına giriyor. */
    return maddeEkle(host, {
      anchor: madde.handle,
      nere: "after",
      baslik: madde.baslik,
      seviye: madde.seviye,
    });
  }
  return maddeEkle(host, {
    anchor: baslikCapasi,
    nere: "before",
    baslik: madde.baslik,
    seviye: madde.seviye,
  });
}
